/*
 * // Created on 2023-11-22
 * // Created by Vitaut Tryputsin
 * //
 * // Copyright (c) 2023 OPEN CASCADE SAS
 * //
 * // This file is part of commercial software by OPEN CASCADE SAS,
 * // furnished in accordance with the terms and conditions of the contract
 * // and with the inclusion of this copyright notice.
 * // This file or any part thereof may not be provided or otherwise
 * // made available to any third party.
 * //
 * // No ownership title to the software is transferred hereby.
 * //
 * // OPEN CASCADE SAS makes no representation or warranties with respect to the
 * // performance of this software, and specifically disclaims any responsibility
 * // for any damages, special or consequential, connected with its use.
 *
 */
import { Vec3Tools } from "../../../core/geometry/vec3-tools.js";
import { Mat4Tools } from "../../../core/geometry/mat4-tools.js";
import { PlaneTools } from "../../../core/geometry/plane-tools.js";
/*
  * Stem placement type
  */
export var StemPlacementType;
(function (StemPlacementType) {
    StemPlacementType[StemPlacementType["Type"] = 0] = "Type";
    StemPlacementType[StemPlacementType["Size"] = 1] = "Size";
    StemPlacementType[StemPlacementType["Offset"] = 2] = "Offset";
})(StemPlacementType || (StemPlacementType = {}));
/**
 * Stem head utilities
*/
export class StemHeadUtilities {
    /**
     * Check validity of stem
     * @param theStemInfo - stem info
     * @returns true if stem is valid
     */
    static isValidStem(theStemInfo) {
        return !Vec3Tools.isEqual(theStemInfo.stemNeckPoint, theStemInfo.headEndPoint)
            && !Vec3Tools.isEqual(theStemInfo.stemNeckPoint, theStemInfo.stemEndPoint)
            && !Vec3Tools.isEqual(theStemInfo.headEndPoint, theStemInfo.stemEndPoint)
            && !Vec3Tools.isEqual(theStemInfo.resectionPlane.normal, { x: 0.0, y: 0.0, z: 0.0 })
            && theStemInfo.scale > 0.0;
    }
    /**
     * Check validity of head
     * @param theHeadInfo - head info
     * @returns true if head is valid
     */
    static isValidHead(theHeadInfo) {
        return theHeadInfo.scale > 0.0
            && !Vec3Tools.isEqual(theHeadInfo.stemInterfacePlane.normal, { x: 0.0, y: 0.0, z: 0.0 });
    }
    /**
   * Check validity of insert
   * @param theInsertInfo - insert info
   * @returns true if insert is valid
   */
    static isValidInsert(theInsertInfo) { return theInsertInfo.scale > 0.0; }
    /**
     * Compute stem transform
     * @param theStem - stem info
     * @returns stem transform based on stem head point in the same position and stem end point in the same position
     */
    static computeStemRelativeTransform(theNewStem, theNewHeadInfo, theOldStem, theOldHeadInfo, theOldStemTransform) {
        let calcDelta = (theOldPosition, theNewPosition) => {
            let delta = Vec3Tools.subtract(theOldPosition, theNewPosition);
            let rotation = Mat4Tools.getRotation(theOldStemTransform);
            let newDelta = Vec3Tools.applyMatrix(rotation, delta);
            return newDelta;
        };
        let anOldHeadCosTransform = this.computeHeadTransform(theOldHeadInfo, theOldStem, theOldStemTransform);
        let aNewHeadCosTransform = this.computeHeadTransform(theNewHeadInfo, theNewStem, theOldStemTransform);
        let headOffset = Vec3Tools.subtract(Mat4Tools.getTranslation(anOldHeadCosTransform), Mat4Tools.getTranslation(aNewHeadCosTransform));
        let stemOffset = calcDelta(theOldStem.stemEndPoint, theNewStem.stemEndPoint);
        return [
            Mat4Tools.translation(headOffset),
            Mat4Tools.translation(Vec3Tools.subtract(headOffset, stemOffset))
        ];
    }
    /**
     * Compute head transform
     * @param theHead - head info
     * @param theStem - stem info
     * @param theStemTransform - stem transform
     * @returns head transform
     */
    static computeHeadTransform(theHead, theStem, theStemTransform) {
        if (!this.isValidHead(theHead) || !this.isValidStem(theStem)) {
            console.error("Error: computeHeadTransform - implant data are not valid");
            return Mat4Tools.identity();
        }
        const headZaxis = Vec3Tools.normalize(Vec3Tools.subtract(theStem.stemNeckPoint, theStem.headEndPoint));
        const headYaxis = Vec3Tools.normalize(Vec3Tools.subtract(theStem.headEndPoint, theStem.stemEndPoint));
        const headXaxis = Vec3Tools.normalize(Vec3Tools.cross(headYaxis, headZaxis));
        //! Project head COS origin on the stem interface plane
        const projPoint = PlaneTools.project({ x: 0, y: 0, z: 0 }, theHead.stemInterfacePlane);
        const location = theStem.headEndPoint;
        const aHeadDir = Vec3Tools.normalize(Vec3Tools.subtract(theStem.headEndPoint, theStem.stemNeckPoint));
        const aStemZAxis = Vec3Tools.normalize(Vec3Tools.subtract(theStem.stemEndPoint, theStem.stemNeckPoint));
        const aStemXAxis = Vec3Tools.normalize(Vec3Tools.cross(aHeadDir, aStemZAxis));
        //! Local coordinate system of the stem
        const stemCoordinateSystem = {
            location: theStem.stemNeckPoint,
            xDirection: aStemXAxis,
            direction: aStemZAxis,
        };
        //! Local coordinate system of the head
        const headInitialCS = {
            location: { x: 0, y: 0, z: 0 },
            xDirection: { x: 1, y: 0, z: 0 },
            direction: { x: 0, y: 0, z: 1 },
        };
        //! Coordinate system of the head in the stem space
        const headCoordinateSystem = {
            location: theStem.stemNeckPoint,
            xDirection: headXaxis,
            direction: headZaxis,
        };
        const aHeadCS = Mat4Tools.getCSRelativeTransformation(stemCoordinateSystem, headCoordinateSystem);
        let aHeadTransform = Mat4Tools.multiply(Mat4Tools.translation(location), aHeadCS);
        //! Get relative transformation from head local coordinate system to
        //! head coordinate system in the stem space
        const aHeadRelativeTransform = Mat4Tools.getCSRelativeTransformation(headInitialCS, headCoordinateSystem);
        //! Calculate head interface plane offset
        const aHeadInterfacePlaneOffset = Vec3Tools.applyMatrix(aHeadRelativeTransform, Vec3Tools.multiply(projPoint, -1));
        //! Apply head interface plane offset to the head transform
        aHeadTransform = Mat4Tools.multiply(Mat4Tools.translation(aHeadInterfacePlaneOffset), aHeadTransform);
        const aHeadScale = Mat4Tools.scale(theHead.scale);
        const aStemTransformation = Mat4Tools.unscale(theStemTransform);
        const aTransform = Mat4Tools.multiply(aStemTransformation, Mat4Tools.multiply(aHeadTransform, aHeadScale));
        return aTransform;
    }
    /**
     * Compute stem resection planes
     * @param theStem - stem info
     * @param theStemTransform - stem transform
     */
    static computeStemResectionPlanes(theStem, theStemTransform) {
        if (!this.isValidStem(theStem)) {
            console.error("Error: computeStemResectionPlanes - implant data are not valid");
            return [PlaneTools.xy(), PlaneTools.yz()];
        }
        //! Get stem end neck plane
        const zDir = Vec3Tools.normalize(Vec3Tools.subtract(theStem.stemEndPoint, theStem.stemNeckPoint));
        const xDir = Vec3Tools.normalize(Vec3Tools.subtract(theStem.headEndPoint, theStem.stemNeckPoint));
        const yDir = Vec3Tools.normalize(Vec3Tools.cross(zDir, xDir));
        const aStemEndNeckPlaneNormal = Vec3Tools.normalize(Vec3Tools.cross(yDir, zDir));
        let aNormal = theStem.resectionPlane.normal;
        if (aNormal.z > 0) {
            aNormal = Vec3Tools.multiply(aNormal, -1);
        }
        //! project stemNeckPoint on the resection plane
        const aProjectPoint = PlaneTools.project(theStem.stemNeckPoint, theStem.resectionPlane);
        let resectionPlane = {
            location: aProjectPoint,
            normal: aNormal,
        };
        let aStemEndNeckPlane = {
            location: theStem.stemNeckPoint,
            normal: aStemEndNeckPlaneNormal,
        };
        //! transform planes
        const aRotationPart = Mat4Tools.getRotation(theStemTransform);
        const aTranslationPart = Mat4Tools.translation(Mat4Tools.getTranslation(theStemTransform));
        const aStemTransform = Mat4Tools.multiply(aTranslationPart, aRotationPart);
        resectionPlane = {
            location: Vec3Tools.applyMatrix(aStemTransform, resectionPlane.location),
            normal: Vec3Tools.multiply(Vec3Tools.normalize(Vec3Tools.applyMatrix(aRotationPart, resectionPlane.normal)), -1),
        };
        aStemEndNeckPlane = {
            location: Vec3Tools.applyMatrix(aStemTransform, aStemEndNeckPlane.location),
            normal: Vec3Tools.multiply(Vec3Tools.normalize(Vec3Tools.applyMatrix(aRotationPart, aStemEndNeckPlane.normal)), -1),
        };
        return [resectionPlane, aStemEndNeckPlane];
    }
    /**
     * Get 3 point plane
     * @param thePoint1 - point 1
     * @param thePoint2 - point 2
     *
     */
    static get3PointPlane(thePoint1, thePoint2, thePoint3) {
        const aLocation = thePoint1;
        const aDirection = Vec3Tools.normalize(Vec3Tools.cross(Vec3Tools.subtract(thePoint2, thePoint1), Vec3Tools.subtract(thePoint3, thePoint1)));
        return { location: aLocation, normal: aDirection };
    }
    /**
     * Stem coordinate system
    */
    static get stemCoordinateSystem() {
        return {
            location: { x: 0, y: 0, z: 0 },
            xDirection: { x: 0, y: 1, z: 0 },
            direction: { x: 0, y: 0, z: -1 },
        };
    }
}
